"use client";

import * as React from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { CheckIcon } from "lucide-react";

import { cn } from "@/lib/utils";

/**
 * Visually aligned with the hygenius-pria reference checkbox
 * (../hygenius-pria/src/components/ui/checkbox.tsx): 16px square, neutral
 * border on a background fill, brand fill and white tick when checked, and a
 * heavier 12px tick.
 *
 * The reference's *implementation* is deliberately not ported. It is a
 * hand-rolled `<button role="checkbox">` beside a hidden `<input>` whose
 * onChange is a no-op, which breaks two things www depends on:
 *
 * - Every call site pairs the checkbox with a `<label htmlFor>` (the PRIA
 *   contact and consulting forms) or wraps it in shadcn `<FormControl>`, whose
 *   Radix Slot injects `id` and `aria-describedby`. The reference puts `id` on
 *   the invisible input, so clicking the label toggles nothing, and its narrow
 *   prop interface drops the injected aria attributes.
 * - The `sr-only` input stays in the tab order, so each checkbox would take
 *   two tab stops, only one of them visible.
 *
 * Radix gives the same appearance with none of that, so the Radix root stays.
 * `shadow-xs` and the `aria-invalid:*` classes are dropped rather than shipped
 * dead: both came from www's previous Tailwind-v4-era file rather than the
 * reference, and neither emits CSS under Tailwind 3. The ring is drawn at full
 * opacity in place of the reference's `ring-primary/20`, the same move
 * button.tsx's header explains; its offset is the reference's own. So is the
 * `ring-2` width (`focus-visible:ring-2`) — not a drift from the button's
 * `ring-[3px]`, as each aligned file keeps its reference's ring width.
 */
const Checkbox = React.forwardRef<
	React.ElementRef<typeof CheckboxPrimitive.Root>,
	React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>
>(({ className, ...props }, ref) => (
	<CheckboxPrimitive.Root
		ref={ref}
		className={cn(
			"peer flex h-4 w-4 shrink-0 cursor-pointer items-center justify-center rounded-sm border border-input bg-background ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-white",
			className
		)}
		{...props}
	>
		<CheckboxPrimitive.Indicator className="flex items-center justify-center text-current">
			<CheckIcon className="h-3 w-3" strokeWidth={3} />
		</CheckboxPrimitive.Indicator>
	</CheckboxPrimitive.Root>
));
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox };
